import { useNavigate, useParams } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
    commaNumber,
    formatDate,
    getRecipientName,
    getTransactionStatus
    // getTransactionType
} from '../../../utils/helper';
import Skeleton from '@mui/material/Skeleton';
import { REACT_APP_BASE_URL } from '../../../config';
import ViewTransactionDocument from './shared/ViewTransactionDocument';
import { ViewPaymentReceipt } from './shared/ViewPaymentReceipt';
import { useTransactionDetails } from '../../../customhooks/useTransactionDetails';
import { LoadingCircles } from '../../../components/icons/LoadingCircles';
import ViewTransactionsSummary from './ViewTransactionsSummary';
import PaymentProgress from '../../../components/PaymentProgress';
import { useUserProfile } from '../../../customhooks/useUserProfile';
// import { hasPermission } from '../../../components/dashboard/dashboardpage/utils';
import { CopyText } from '../../../components/CopyText';
import { ChevronLeftIcon } from '../../../components/icons';

const ViewTransaction = () => {
    const navigate = useNavigate();
    const { reference } = useParams();

    const { user } = useSelector((state: RootState) => state.user);

    const {
        isLoading: isTransactionDetailsLoading,
        entries: transactionDetails,
        isFetching
    } = useTransactionDetails(reference, user?.corporate_id);
    const { permissions } = useUserProfile();

    const topSection = [
        {
            label: 'Rate',
            value: `1 ${transactionDetails?.fromCurrencyCode} = ${transactionDetails?.rateValue} ${transactionDetails?.toCurrencyCode}`
        },
        {
            label: 'Amount',
            value: transactionDetails?.amountFrom
        },
        {
            label: 'Status',
            value: getTransactionStatus(transactionDetails?.status),
            isBold: true
        },
        {
            label: 'Tranx ID',
            value: transactionDetails?.transactionReference,
            isBold: true,
            canCopy: true
        }
    ];

    const midSection = [
        {
            label: 'Transaction Start Date',
            value: transactionDetails?.transactionStartDate
                ? formatDate(transactionDetails?.transactionStartDate)
                : 'n/a'
        },
        {
            label: 'Beneficiary receives',
            value: `${transactionDetails?.toCurrencyCode} ${transactionDetails?.amountTo.toLocaleString()}`
        },
        {
            label: 'Transaction account',
            value: `${transactionDetails?.fromCurrencyCode} Account`
        },
        {
            label: 'Amount from',
            value: `${transactionDetails?.fromCurrencyCode} ${transactionDetails?.amountFrom.toLocaleString()}`
        },
        {
            label: 'Amount to',
            value: `${transactionDetails?.toCurrencyCode} ${transactionDetails?.amountTo.toLocaleString()}`
        },
        {
            label: 'Transaction End Date',
            value: transactionDetails?.transactionEndDate
                ? formatDate(transactionDetails?.transactionEndDate)
                : 'n/a'
        }
    ];

    return (
        <div className='lg:h-screen'>
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
                open={isTransactionDetailsLoading || isFetching}
            >
                <div className='animate-spin'>
                    <LoadingCircles />
                </div>
            </Backdrop>

            <div className='hidden lg:block mb-[1.3rem]'>
                <button
                    className='flex items-center gap-2 text-lg font-normal text-ebony cursor-pointer w-fit'
                    onClick={() => {
                        navigate('/payments/transactions');
                    }}
                >
                    <ChevronLeftIcon />
                    <span>All transactions</span>
                </button>
            </div>

            <div className='lg:hidden p-[16px] bg-white'>
                <button
                    className='flex items-center gap-2 text-lg font-normal text-ebony cursor-pointer w-fit'
                    onClick={() => {
                        navigate('/payments/transactions');
                    }}
                >
                    <ChevronLeftIcon />
                    <span>All transactions</span>
                </button>
            </div>

            {isTransactionDetailsLoading || isFetching ? (
                <Skeleton variant='text' sx={{ fontSize: '8rem' }} />
            ) : (
                <ViewTransactionsSummary {...{ transactionDetails }} />
            )}

            {isTransactionDetailsLoading || isFetching ? (
                <div className='mt-[-5rem]'>
                    <Skeleton variant='text' sx={{ fontSize: '20rem' }} />
                </div>
            ) : (
                <>
                    <div className='hidden lg:block'>
                        <div className='flex '>
                            <div className='w-[50%] border-r border-gray300'>
                                <div className='pt-[24px] pb-[80px] px-[16px] max-w-[455px]'>
                                    <h5 className='text-base font-medium text-ebony border border-0 border-b border-appcolorfaintnine mb-[8px]'>
                                        Transaction details
                                    </h5>

                                    <div className='pb-[16px]'>
                                        {topSection?.map((item) => (
                                            <div className='flex justify-between items-center leading-[24px] text-ebony text-sm mt-[16px] border border-0 border-b border-appcolorfaintnine leading-6'>
                                                <p>{item.label}</p>
                                                <p
                                                    className={`items-center gap-[4px] flex ${
                                                        item.isBold
                                                            ? 'font-bold'
                                                            : ''
                                                    }`}
                                                >
                                                    {item.value}{' '}
                                                    {item.canCopy && (
                                                        <CopyText
                                                            text={
                                                                item.value || ''
                                                            }
                                                        />
                                                    )}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    <div className='flex justify-center my-[16px] h-[24px] items-center'>
                                        <p className='w-[56px] border border-0 border-b border-silver'></p>
                                    </div>

                                    <div className='pb-[16px] mb-[16px]'>
                                        {midSection?.map((item) => (
                                            <div className='flex justify-between items-center leading-[24px] text-ebony text-sm font-normal mt-[16px] border border-0 border-b border-appcolorfaintnine leading-6'>
                                                <p>{item.label}</p>
                                                <p>{item.value} </p>
                                            </div>
                                        ))}
                                    </div>

                                    <div className='flex justify-center my-[16px] h-[24px] items-center'>
                                        <p className='w-[56px] border border-0 border-b border-silver'></p>
                                    </div>

                                    <div className='text-ebony text-sm font-normal leading-6'>
                                        <p>Narration</p>
                                        <p>{transactionDetails?.narration}</p>
                                    </div>
                                    {transactionDetails && (
                                        <>
                                            <div className='mt-[16px]'>
                                                <p className='text-steelBlue text-sm font-normal'>
                                                    Support Document
                                                </p>

                                                <ViewTransactionDocument
                                                    fileUrl={
                                                        REACT_APP_BASE_URL +
                                                        transactionDetails?.documents[0]?.documentUrl?.replace(
                                                            'v1/',
                                                            ''
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className='mt-[16px]'>
                                                <p className='text-steelBlue text-sm font-normal'>
                                                    Payment Receipt:
                                                </p>

                                                <ViewPaymentReceipt
                                                    data={transactionDetails}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {transactionDetails?.transactionAction !==
                                'CREDIT' && (
                                <div className='w-[50%] pl-[24px] pt-[32px]'>
                                    <PaymentProgress
                                        fromAmount={`${transactionDetails?.fromCurrencyCode} ${commaNumber(
                                            Number(
                                                transactionDetails?.amountFrom
                                            ).toFixed(2)
                                        )}`}
                                        toAmount={`${transactionDetails?.toCurrencyCode} ${commaNumber(
                                            Number(
                                                transactionDetails?.amountTo
                                            ).toFixed(2)
                                        )}`}
                                        toName={
                                            transactionDetails?.transactionAction ===
                                            'CREDIT'
                                                ? user?.name
                                                : getRecipientName({
                                                      transactionDetails,
                                                      user
                                                  })
                                        }
                                        status={transactionDetails?.status}
                                        transactionAction={
                                            transactionDetails?.transactionAction ??
                                            ''
                                        }
                                        fromName={
                                            transactionDetails?.businessName
                                        }
                                    />

                                    <div className='mt-4 border border-gray px-6 py-4 rounded-[16px]'>
                                        <p className='text-gray700 leading-[24px]'>
                                            You are Sending{' '}
                                            <span className='text-black font-semibold'>
                                                {
                                                    transactionDetails?.fromCurrencyCode
                                                }{' '}
                                                {commaNumber(
                                                    Number(
                                                        transactionDetails?.amountFrom
                                                    ).toFixed(2)
                                                )}
                                            </span>{' '}
                                            at 1 &nbsp;
                                            {
                                                transactionDetails?.fromCurrencyCode
                                            }
                                            &nbsp; ={' '}
                                            {Number(
                                                transactionDetails?.rateValue
                                            )}
                                            &nbsp;
                                            {transactionDetails?.toCurrencyCode}
                                            .
                                        </p>

                                        <p className='text-gray700 leading-[24px]'>
                                            Your Recipient will get{' '}
                                            <span className='text-black font-semibold'>
                                                {
                                                    transactionDetails?.toCurrencyCode
                                                }{' '}
                                                {commaNumber(
                                                    Number(
                                                        transactionDetails?.amountTo
                                                    ).toFixed(2)
                                                )}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='lg:hidden pt-[24px] pb-[80px] px-[16px]'>
                        <h5 className='text-base font-medium text-ebony border border-0 border-b border-appcolorfaintnine mb-[8px]'>
                            Transaction details
                        </h5>

                        <div className='pb-[16px]'>
                            {topSection?.map((item) => (
                                <div className='flex justify-between items-center leading-[24px] text-ebony text-sm mt-[16px] border border-0 border-b border-appcolorfaintnine leading-6'>
                                    <p>{item.label}</p>
                                    <p
                                        className={`items-center gap-[4px] flex ${
                                            item.isBold ? 'font-bold' : ''
                                        }`}
                                    >
                                        {item.value}{' '}
                                        {item.canCopy && (
                                            <CopyText text={item.value || ''} />
                                        )}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className='flex justify-center my-[16px] h-[24px] items-center'>
                            <p className='w-[56px] border border-0 border-b border-silver'></p>
                        </div>

                        <div className='pb-[16px] mb-[16px]'>
                            {midSection?.map((item) => (
                                <div className='flex justify-between items-center leading-[24px] text-ebony text-sm font-normal mt-[16px] border border-0 border-b border-appcolorfaintnine leading-6'>
                                    <p>{item.label}</p>
                                    <p>{item.value} </p>
                                </div>
                            ))}
                        </div>

                        <div className='flex justify-center my-[16px] h-[24px] items-center'>
                            <p className='w-[56px] border border-0 border-b border-silver'></p>
                        </div>

                        <div className='text-ebony text-sm font-normal leading-6'>
                            <p>Narration</p>
                            <p>{transactionDetails?.narration || '-'}</p>
                        </div>
                        {transactionDetails && (
                            <>
                                <div className='mt-[16px]'>
                                    <p className='text-steelBlue text-sm font-normal'>
                                        Support Document
                                    </p>

                                    <ViewTransactionDocument
                                        fileUrl={
                                            REACT_APP_BASE_URL +
                                            transactionDetails?.documents[0]?.documentUrl?.replace(
                                                'v1/',
                                                ''
                                            )
                                        }
                                    />
                                </div>

                                <div className='mt-[16px]'>
                                    <p className='text-steelBlue text-sm font-normal'>
                                        Payment Receipt:
                                    </p>

                                    <ViewPaymentReceipt
                                        data={transactionDetails}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ViewTransaction;
